import { useTranslation } from 'react-i18next';
import { useEffect, useReducer, useRef, useState } from 'react';
import { Flex, Segmented, Skeleton, Typography } from 'antd';
import { DownOutlined, RightOutlined } from '@ant-design/icons';
import ReadingTypeSelectBox from 'components/molecules/reading-type-select-box';
import BaseButton from 'components/atoms/baseButton';
import {
  ActivityStatus,
  ModuleType,
  type ReadingType,
} from 'infrastructure/enums';
import { usePatientActivity } from 'infrastructure/hooks/rpm/activity/use-patient-activity';
import useUser from 'utils/useUser';
import BaseCheckbox from 'components/atoms/base-checkbox';
import { safeClamp } from 'utils/helpers/safeClamp';
import BasePagination from 'components/atoms/base-pagination';
import { ELASTIC_SEARCH_MAX_ITEMS } from 'constants/count';
import ActivityTableBulkModal from 'components/molecules/activity-table-bulk-modal';
import PatientReadingTemplateModal from 'components/molecules/patient-reading-template';
import { useActivityBulkModal } from 'components/organisms/patient-activity/hooks/bulkComment/use-bulk-comment';

import {
  checkCurrentTemplateRole,
  getActivityType,
  getCriticalIndicatorColor,
  getNormalAndAtRiskIndicatorColor,
} from './utils';
import ActivityCard from './activity-card';
import SegmentedLabel from './segmented-label';
import s from './styles.module.scss';
import { scrollWithOffset } from '../patient-chart-nav-section';
import { SCROLLING_DURATION } from '../patient-chart-nav-section/constants';

import type { FC } from 'react';
import type { IRpmActivityCountResponse } from 'infrastructure/interfaces/i-rpm-patient-activity';

const { Text } = Typography;

const PATIENT_ACTIVITY_PAGE_SIZE = 10;

interface PatientChartActivityProps {
  patientGuid: string;
  activityCountByStatus?: IRpmActivityCountResponse;
  onReadingsRefetch?: () => void;
}

const PatientChartActivity: FC<PatientChartActivityProps> = ({
  patientGuid,
  activityCountByStatus,
  onReadingsRefetch,
}) => {
  const {
    isAgencyDoctor,
    isFullyManagedAgency,
    hasRNRole,
    isMedicalAssistantRole,
  } = useUser();
  const isFullyManagedMa =
    isAgencyDoctor && isMedicalAssistantRole && isFullyManagedAgency;
  const isFullyManagedRn = isAgencyDoctor && hasRNRole && isFullyManagedAgency;
  const currentTemplateRole = checkCurrentTemplateRole(
    isFullyManagedMa,
    isFullyManagedRn,
  );

  const [activeActivityType, setActiveActivityType] = useState<
    'unactioned' | 'actioned'
  >('unactioned');
  const [activeActivityStatus, setActiveActivityStatus] = useState<
    'critical' | 'normalAtRisk'
  >('critical');

  const [selectedReadingType, setSelectedReadingType] = useState<
    ReadingType | undefined
  >(undefined);
  const [isShowOnlyErroneous, setIsShowOnlyErroneous] = useState(false);

  const [isExpanded, setIsExpanded] = useState(true);
  const { t } = useTranslation();

  const pageChangeTimeoutRef = useRef<number | null>(null);

  const { toggleOpen, opened } = useActivityBulkModal();
  const [openTemplateModal, toggleTemplateModal] = useReducer((v) => !v, false);

  const {
    activity,
    queryKey,
    Alert,
    isLoading,
    isFetching,
    page,
    status,
    setPage,
    handleChangeType,
    hasUnsavedMessage,
    showAlert,
    refetch,
    resetActions,
  } = usePatientActivity({
    patientGuid,
    pageSize: PATIENT_ACTIVITY_PAGE_SIZE,
    readingType: selectedReadingType,
    onlyIsErroneous: isShowOnlyErroneous,
    onReadingsRefetch,
  });

  const handleActivityTypeChange = async (value: 'unactioned' | 'actioned') => {
    const newActivityType = getActivityType(
      value,
      activeActivityStatus,
      isAgencyDoctor,
      isFullyManagedAgency,
    );
    const success = await handleChangeType(newActivityType);
    if (success) {
      setActiveActivityType(value);
    }
  };

  const handleActivityStatusChange = async (
    value: 'critical' | 'normalAtRisk',
  ) => {
    const newActivityType = getActivityType(
      activeActivityType,
      value,
      isAgencyDoctor,
      isFullyManagedAgency,
    );

    const success = await handleChangeType(newActivityType);
    if (success) {
      setActiveActivityStatus(value);
    }
  };

  const handleErroneousCheckboxChange = async (checked: boolean) => {
    if (hasUnsavedMessage) {
      const answer = await showAlert({
        title: t('confirm.title'),
        messageTitle: t('messages.confirmUnsavedChangesTitle'),
        message: t('messages.confirmUnsavedChangesMessage'),
        successBtnLabel: t('controls.yes'),
        rejectBtnLabel: t('controls.no'),
      });
      if (!answer) return;
      resetActions();
    }

    setIsShowOnlyErroneous(checked);
  };

  const activityCount = activity?.count ?? 0;
  const isNotErroneousActivityCount =
    activity?.data.filter((item) => !item.isErroneous).length ?? 0;

  const isNormalRisk =
    status === ActivityStatus.UnactionedNormalRisk && activityCount > 0;

  const canShowBulkActions =
    isNormalRisk &&
    !isShowOnlyErroneous &&
    isNotErroneousActivityCount > 0 &&
    !isLoading;
  const showBulkCommentBtn = canShowBulkActions && currentTemplateRole === null;
  const showBulkTemplateCommentBtn =
    canShowBulkActions && currentTemplateRole !== null;

  const handlePageChange = (newPage: number) => {
    if (pageChangeTimeoutRef.current) {
      clearTimeout(pageChangeTimeoutRef.current);
    }

    const element = document.getElementById('combine-activity');
    if (element) {
      scrollWithOffset(element, true);
    }

    pageChangeTimeoutRef.current = window.setTimeout(() => {
      setPage(newPage);
      pageChangeTimeoutRef.current = null;
    }, SCROLLING_DURATION + 100);
  };

  useEffect(() => {
    return () => {
      if (pageChangeTimeoutRef.current) {
        clearTimeout(pageChangeTimeoutRef.current);
      }
    };
  }, []);

  return (
    <div className={s.wrapper} id="combine-activity">
      <div className={s['header-wrapper']}>
        <div className={s['header-left-wrapper']}>
          <BaseButton
            type="secondary"
            className={s['toggle-button']}
            icon={isExpanded ? <DownOutlined /> : <RightOutlined />}
            onClick={() => setIsExpanded(!isExpanded)}
          />
          <h4 className={s.title}>{t('labels.activity')}</h4>
          <Segmented
            options={[
              {
                label: (
                  <SegmentedLabel
                    text={t('labels.unactioned')}
                    indicatorColor={
                      isFullyManagedAgency && isAgencyDoctor
                        ? ''
                        : getCriticalIndicatorColor(activityCountByStatus)
                    }
                  />
                ),
                value: 'unactioned',
              },
              {
                label: t('labels.actioned'),
                value: 'actioned',
              },
            ]}
            value={activeActivityType}
            onChange={(value) =>
              handleActivityTypeChange(value as 'unactioned' | 'actioned')
            }
          />
          {isFullyManagedAgency &&
            isAgencyDoctor &&
            activeActivityType === 'unactioned' && (
              <>
                <div className={s.divider} />
                <Segmented
                  options={[
                    {
                      label: (
                        <SegmentedLabel
                          text={t('labels.critical')}
                          indicatorColor={getCriticalIndicatorColor(
                            activityCountByStatus,
                          )}
                        />
                      ),
                      value: 'critical',
                    },
                    {
                      label: (
                        <SegmentedLabel
                          text={`${t('labels.normal')}/${t('labels.at-risk')}`}
                          indicatorColor={getNormalAndAtRiskIndicatorColor(
                            activityCountByStatus,
                          )}
                        />
                      ),
                      value: 'normalAtRisk',
                    },
                  ]}
                  value={activeActivityStatus}
                  onChange={(value) =>
                    handleActivityStatusChange(
                      value as 'critical' | 'normalAtRisk',
                    )
                  }
                />
              </>
            )}
        </div>
        <Flex align="center" gap={12}>
          {showBulkCommentBtn && (
            <BaseButton
              label={t('labels.bulkReadingsComment')}
              dataCy="addNote-button"
              onClick={toggleOpen}
            />
          )}
          {showBulkTemplateCommentBtn && (
            <BaseButton
              label={t('labels.templatesModal')}
              dataCy="addNote-button"
              onClick={toggleTemplateModal}
            />
          )}
          <BaseCheckbox
            placeholder={t('labels.showErroneusOnly')}
            value={isShowOnlyErroneous}
            onChange={handleErroneousCheckboxChange}
          />
          <ReadingTypeSelectBox
            value={selectedReadingType}
            onChange={(v) => setSelectedReadingType(v)}
            className={s['reading-type-select']}
            withAll
            allOptionLabel={t('labels.allReadingTypes')}
            closeOnScroll
          />
        </Flex>
      </div>
      {isExpanded && (
        <div className={s.body}>
          <Text className={s['total-readings']}>
            {`${t('labels.totalReadings')}: `}
            {isLoading || isFetching ? (
              <Skeleton.Node active className={s['skeleton-count']} />
            ) : (
              activity?.count || 0
            )}
          </Text>
          {isLoading || isFetching ? (
            <>
              <Skeleton.Node active className={s['skeleton-item']} />
              <Skeleton.Node active className={s['skeleton-item']} />
              <Skeleton.Node active className={s['skeleton-item']} />
            </>
          ) : (
            <>
              {activity?.data.map((record) => (
                <ActivityCard
                  key={record.id}
                  activityData={record}
                  showReviewCol={status !== ActivityStatus.Erroneous}
                  hideReviewButton={
                    status === ActivityStatus.Actioned ||
                    status === ActivityStatus.UnactionedNormalRisk ||
                    isShowOnlyErroneous
                  }
                  view="patient"
                  getActivityQueryKey={queryKey}
                  module={ModuleType.RPMCCM}
                  type={status}
                />
              ))}
            </>
          )}
          {activityCount > PATIENT_ACTIVITY_PAGE_SIZE &&
            !isLoading &&
            !isFetching && (
              <BasePagination
                page={page ?? 1}
                total={safeClamp(activityCount, 0, ELASTIC_SEARCH_MAX_ITEMS)}
                align="end"
                onChange={handlePageChange}
                pageSize={PATIENT_ACTIVITY_PAGE_SIZE}
                hide={false}
                showSizeChanger={false}
              />
            )}
        </div>
      )}
      {Alert}
      {opened && (
        <ActivityTableBulkModal
          patientGuid={patientGuid}
          open={opened}
          onClose={toggleOpen}
          activityCount={isNotErroneousActivityCount}
        />
      )}
      {openTemplateModal && currentTemplateRole !== null && (
        <PatientReadingTemplateModal
          open={openTemplateModal}
          templateRole={currentTemplateRole}
          onClose={toggleTemplateModal}
          patientGuid={patientGuid}
          type={status}
          onReadingsRefetch={onReadingsRefetch}
          refetchActivity={refetch}
        />
      )}
    </div>
  );
};
export default PatientChartActivity;
